import { useState } from "react";
import { Search, Filter, ChevronDown, X } from "lucide-react";
import { Button, Input, Select, type SelectOption } from "@/components/ui";
import { cn } from "@/utils/format";
import type { VehicleSearchParams, BodyType, FuelType } from "@/types";

export interface SearchFiltersProps {
  filters: VehicleSearchParams;
  onFiltersChange: (filters: VehicleSearchParams) => void;
  resultCount?: number;
}

const makeOptions: SelectOption[] = [
  { value: "", label: "All Makes" },
  { value: "toyota", label: "Toyota" },
  { value: "honda", label: "Honda" },
  { value: "ford", label: "Ford" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "bmw", label: "BMW" },
  { value: "mercedes-benz", label: "Mercedes-Benz" },
  { value: "audi", label: "Audi" },
  { value: "tesla", label: "Tesla" },
  { value: "lexus", label: "Lexus" },
  { value: "mazda", label: "Mazda" },
];

const bodyTypeOptions: SelectOption[] = [
  { value: "", label: "All Body Types" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "truck", label: "Truck" },
  { value: "coupe", label: "Coupe" },
  { value: "convertible", label: "Convertible" },
  { value: "hatchback", label: "Hatchback" },
  { value: "wagon", label: "Wagon" },
  { value: "van", label: "Van" },
];

const fuelTypeOptions: SelectOption[] = [
  { value: "", label: "All Fuel Types" },
  { value: "gasoline", label: "Gasoline" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Hybrid" },
  { value: "plug_in_hybrid", label: "Plug-in Hybrid" },
];

const sortOptions: SelectOption[] = [
  { value: "newest", label: "Newest Listed" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "mileage_asc", label: "Mileage: Low to High" },
  { value: "year_desc", label: "Year: New to Old" },
];

const currentYear = new Date().getFullYear();
const yearOptions: SelectOption[] = [
  { value: "", label: "Any Year" },
  ...Array.from({ length: 20 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  })),
];

export function SearchFilters({
  filters,
  onFiltersChange,
  resultCount,
}: SearchFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.query || "");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltersChange({ ...filters, query: searchInput, page: 1 });
  };

  const updateFilter = <K extends keyof VehicleSearchParams>(
    key: K,
    value: VehicleSearchParams[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value, page: 1 });
  };

  const clearFilters = () => {
    setSearchInput("");
    onFiltersChange({ page: 1, pageSize: filters.pageSize });
  };

  const hasActiveFilters =
    filters.query ||
    filters.makes?.length ||
    filters.minYear ||
    filters.maxPrice ||
    filters.maxMileage ||
    filters.bodyTypes?.length ||
    filters.fuelTypes?.length ||
    filters.certified;

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="search"
            placeholder="Search by make, model, or keyword..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit">Search</Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Filters
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              showFilters && "rotate-180",
            )}
          />
        </Button>
      </form>

      {/* Result count and sort */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {resultCount !== undefined && (
            <>
              <span className="font-medium">
                {resultCount.toLocaleString()}
              </span>{" "}
              vehicles found
            </>
          )}
        </p>
        <div className="w-48">
          <Select
            options={sortOptions}
            value={filters.sortBy || "newest"}
            onChange={(e) =>
              updateFilter(
                "sortBy",
                e.target.value as VehicleSearchParams["sortBy"],
              )
            }
          />
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 animate-slide-down">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              label="Make"
              options={makeOptions}
              value={filters.makes?.[0] || ""}
              onChange={(e) =>
                updateFilter(
                  "makes",
                  e.target.value ? [e.target.value] : undefined,
                )
              }
            />
            <Select
              label="Body Type"
              options={bodyTypeOptions}
              value={filters.bodyTypes?.[0] || ""}
              onChange={(e) =>
                updateFilter(
                  "bodyTypes",
                  e.target.value ? [e.target.value as BodyType] : undefined,
                )
              }
            />
            <Select
              label="Fuel Type"
              options={fuelTypeOptions}
              value={filters.fuelTypes?.[0] || ""}
              onChange={(e) =>
                updateFilter(
                  "fuelTypes",
                  e.target.value ? [e.target.value as FuelType] : undefined,
                )
              }
            />
            <Select
              label="Minimum Year"
              options={yearOptions}
              value={filters.minYear?.toString() || ""}
              onChange={(e) =>
                updateFilter(
                  "minYear",
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
            />
            <Input
              label="Max Price"
              type="number"
              placeholder="$100,000"
              value={filters.maxPrice || ""}
              onChange={(e) =>
                updateFilter(
                  "maxPrice",
                  e.target.value ? parseInt(e.target.value) * 100 : undefined,
                )
              }
            />
            <Input
              label="Max Mileage"
              type="number"
              placeholder="100,000"
              value={filters.maxMileage || ""}
              onChange={(e) =>
                updateFilter(
                  "maxMileage",
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.certified || false}
                  onChange={(e) =>
                    updateFilter("certified", e.target.checked || undefined)
                  }
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Certified Only
                </span>
              </label>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button type="button" variant="ghost" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
